import { useMemo, useReducer } from 'react';
import { DateTime } from 'luxon';
import { Lookup, RawGoal } from '@views';
import { ActionResponse } from '@actions/ActionResponse';
import {
  canAddMore,
  canSubmitForm,
  hasProgress,
  initialSmartGoalState,
  isFormDuplicate,
  smartGoalReducer,
} from './smartGoalFlow';
import {
  GoalActivity,
  MAX_ACTIVITIES,
  colorForIndex,
  goalPeriodEnd,
  goalPeriodStart,
  toRawGoals,
} from './smartGoalUtils';

export interface SmartGoalDialogProps {
  open: boolean;
  cohortUserId: number;
  groupStartDate: DateTime;
  weekNumberIndexZero: number;
  activities: Lookup[];
  timeframes: Lookup[];
  saveGoals: (goals: RawGoal[]) => Promise<ActionResponse>;
  onClose: () => void;
}

export type ActivityCardView = GoalActivity & {
  index: number;
  name: string;
  color: string;
};

export const SAVE_ERROR_MESSAGE = "We couldn't save your goal. Please try again.";

export const useSmartGoalDialogController = ({
  cohortUserId,
  groupStartDate,
  weekNumberIndexZero,
  activities: activityLookup,
  timeframes,
  saveGoals,
  onClose,
}: SmartGoalDialogProps) => {
  const [state, dispatch] = useReducer(smartGoalReducer, initialSmartGoalState);

  const startDate = goalPeriodStart(groupStartDate, weekNumberIndexZero);
  const endDate = goalPeriodEnd(startDate);

  const activityOptions = useMemo(
    () => [...activityLookup].sort((a, b) => a.name.localeCompare(b.name)),
    [activityLookup]
  );
  const activityName = (id: number) => activityLookup.find((a) => a.id === id)?.name || '';

  const cards: ActivityCardView[] = state.activities.map((a, index) => ({
    ...a,
    index,
    name: activityName(a.activityId),
    color: colorForIndex(index),
  }));

  const close = () => {
    dispatch({ type: 'reset' });
    onClose();
  };

  const confirmAndSave = async () => {
    if (state.saving) return;
    dispatch({ type: 'saveStarted' });
    const response = await saveGoals(
      toRawGoals(state.activities, { cohortUserId, startDate, activities: activityLookup, timeframes })
    );
    if (response.status === 'SUCCESS') {
      dispatch({ type: 'saveSucceeded' });
    } else {
      dispatch({ type: 'saveFailed', message: SAVE_ERROR_MESSAGE });
    }
  };

  // X button and Escape. Leaving with unsaved progress asks first; once saved, it's just "done".
  const requestClose = () => {
    if (state.saving) return;
    if (state.screen === 'success' || !hasProgress(state)) {
      close();
      return;
    }
    dispatch({ type: 'requestLeave' });
  };

  const count = state.activities.length;
  const deleteTarget = state.deleteConfirmIdx !== null ? cards[state.deleteConfirmIdx] : undefined;

  return {
    state,
    screen: state.screen,
    form: state.form,
    activityOptions,
    cards,
    // On the build screen the card being edited is shown in the form instead of the list.
    buildCards: cards.filter((c) => c.index !== state.editingIdx),
    isEditing: state.editingIdx !== null,
    isDuplicate: isFormDuplicate(state),
    duplicateName: activityName(state.form.activityId),
    canSubmit: canSubmitForm(state),
    canAddMore: canAddMore(state),
    maxActivities: MAX_ACTIVITIES,
    startProgress: 1,
    buildProgress: Math.min(count + 1, MAX_ACTIVITIES),
    celebrateUpdated: state.lastChange === 'updated',
    activityCount: count,
    goalStartLabel: startDate.toFormat('ccc, LLL d'),
    goalEndLabel: endDate.toFormat('ccc, LLL d'),
    deleteTargetName: deleteTarget?.name ?? '',
    deleteConfirmOpen: state.deleteConfirmIdx !== null,
    leaveWarningOpen: state.leaveWarningOpen,
    saving: state.saving,
    saveError: state.saveError,

    setActivity: (activityId: number) => dispatch({ type: 'setForm', patch: { activityId } }),
    setFrequency: (frequency: number) => dispatch({ type: 'setForm', patch: { frequency } }),
    setDuration: (duration: number) => dispatch({ type: 'setForm', patch: { duration } }),
    submitForm: () => dispatch({ type: 'submitForm' }),
    addAnother: () => dispatch({ type: 'addAnother' }),
    review: () => dispatch({ type: 'review' }),
    edit: (index: number) => dispatch({ type: 'edit', index }),
    requestDelete: (index: number) => dispatch({ type: 'requestDelete', index }),
    cancelDelete: () => dispatch({ type: 'cancelDelete' }),
    confirmDelete: () => dispatch({ type: 'confirmDelete' }),
    cancelLeave: () => dispatch({ type: 'cancelLeave' }),
    confirmLeave: close,
    requestClose,
    confirmAndSave,
    finish: close,
  };
};
